import { NgClass } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { LedgerPostsComponent } from 'components/accounting/ledger/ledger-posts.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { ReasonSelectComponent } from 'components/input/reason-select/reason-select.component';
import { ListColumnComponent, ListRowComponent, ListViewComponent, ListViewDirective } from 'components/layout/list-view/list-view.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { LedgerPostType } from 'enums/LedgerPostType';
import { ReasonType } from 'enums/ReasonType';
import { BillingPlanDtoModel } from 'models/generated/Accounting.Api.Models.BillingPlanDto';
import { CreditDtoModel, CreditWizardRequestModel, EndAccountingPlanDtoModel } from 'models/generated/Accounting.Api.Models.CreditWizardRequest';
import { BillingDocumentLineModel, LineType } from 'models/generated/Accounting.IntegrationAbstractions.BillingDocumentLine';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { Layout } from 'modules/layout';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductPlanService } from 'services/productplan.service';

@CredentialRequired(CredentialType.CreateCreditMemo, CredentialType.EditCompanyAccountingSettings)
@Component({
	templateUrl: './credit-wizard.component.html',
	providers: [FormValidationService],
	imports: [Layout, ListViewComponent, ListViewDirective, ListRowComponent, ListColumnComponent, NgClass, CustomInputComponent, DateInputComponent, ReasonSelectComponent, LedgerPostsComponent, CurrencyPipe, TranslatePipe, ProductNamePipe]
})
export class CreditWizardComponent {
	
	public steps = CreditWizardSteps;
	public reasonType = ReasonType;
	public inputType = InputType;
	public loading = false;
	public stepValid = true;

	minEndDate?: Date;
	public today: Date;

	public request = new CreditWizardRequestModel();
	public creditLines?: BillingDocumentLineModel[];
	public accountingPlans: AccountingPlanModel[] = [];
	public productPlans: ProductPlanModel[] = [];
	public get readyToComplete(): boolean { return (this.creditLines != undefined) && (this.states.filter(s => !s.completed).length == 0); }
	private states: StepState[] = [];
	public currentStepState?: StepState;
	public get currentSubStep() { return this.currentStepState?.subStep?.type; }

	private matchingLedgerPosts: LedgerPostModel[] = [];
	public currentlyExemptedFromFees?: boolean;
	private backendPromise?: Promise<void>; // Reference to all backend calls initiated after setting invoice. Only need to be awaited once user has selected lines to credit
	private _invoiceLines?: BillingDocumentLineModel[];
	private _invoiceNumber?: string;
	private _companyId?: string;
	private _brandId?: string;
	public set sourceData(value: { invoiceNumber: string, invoiceLines: BillingDocumentLineModel[], brandId: string, companyId: string }) {
		this._invoiceLines = value.invoiceLines;
		this._invoiceNumber = value.invoiceNumber;
		this._companyId = value.companyId
		this._brandId = value.brandId;
		this.viableInvoiceLines = this.invoiceLines?.filter(l => l.totalNetAmount && l.totalNetAmount > 0) ?? [];

		this.loading = true;
		this.backendPromise = Promise.all([
			this.accounting.getCompanySettings(value.companyId).then(s => { this.currentlyExemptedFromFees = s.find(cs => cs.brandId == this._brandId)?.noHandlingFees; }),
			this.accounting.getLedgerPostsByPostedDocumentNo(value.invoiceNumber, value.brandId).then(posts => { this.matchingLedgerPosts = posts; }),
			this.productPlanService.getAllProductPlansAndAccountingPlansFromCompany(value.companyId).then(plans => { this.productPlans = plans; this.accountingPlans = plans.flatMap(p => p.accountingPlans); })
		]).then(() => {
			this.accountingPlans.forEach(ap => ap.productPlan = this.productPlans.find(pp => pp.id == ap.productPlanId));
			this.viableInvoiceLines?.forEach(line => line.suspendedAccountingPlan = this.isLineSuspended(line));
			this.loading = false 
		});
	}
	public get invoiceLines() { return this._invoiceLines; }
	public get invoiceNumber() { return this._invoiceNumber; }
	public get companyId() { return this._companyId; }
	public get brandId() { return this._brandId; }
	public selectedLines: BillingDocumentLineModel[] = [];
	public viableInvoiceLines?: BillingDocumentLineModel[];

	public get currentLineRequest() { return this.currentStepState?.lineRequest; }
	public set currentLine(targetInvoiceLine: BillingDocumentLineModel | undefined) {
		const state = this.states.find(s => s.invoiceLine === targetInvoiceLine);
		this.currentStepState = state;
		this.minEndDate = state?.accountingPlan?.startDate;
		if (!this.minEndDate || this.minEndDate < this.today)
			this.minEndDate = this.today;
	}
	public get currentLine() { return this.currentStepState?.invoiceLine; }
	public get currentStepIsTrueFalse() {
		return this.currentStepState?.subStep !== undefined ? this.stepDefinitions.get(this.currentStepState?.subStep.type)?.isTrueFalse : false
	}

	@ViewChild(LedgerPostsComponent)
	public ledgerPosts?: LedgerPostsComponent;

	constructor(
		private readonly accounting: AccountingService,
		private readonly productPlanService: ProductPlanService,
		private readonly navigation: NavigationService,
		private readonly translate: TranslatePipe,
		private readonly currency: CurrencyPipe,
		private readonly date: CustomDatePipe,
		private readonly dateService: DateService,
		private readonly notificationService: NotificationService,
		readonly validation: FormValidationService
	) {
		validation.stateChanged.subscribe(state => this.stepValid = validation.isValid());
		this.today = dateService.today();
	}

	public toggleSelectedLine(line: BillingDocumentLineModel) {
		if (line.suspendedAccountingPlan) return;
		const currentIndex = this.selectedLines.indexOf(line);
		if (currentIndex >= 0) this.selectedLines.splice(currentIndex, 1);
		else this.selectedLines.push(line);
	}
	public lockSelectedLines() {
		if (this.creditLines || !this.viableInvoiceLines || this.selectedLines.length === 0) return;

		// lock selected lines in the same order they appear on the invoice
		this.creditLines = this.viableInvoiceLines.filter(l => this.selectedLines.indexOf(l) >= 0);


		// Setting basic states of each line requires data from backend to be loaded
		this.backendPromise?.then(() => {

			if (this.creditLines?.some(line => line.suspendedAccountingPlan)) {
				this.creditLines = undefined;
				this.notificationService.warning('credit-wizard.warning.suspended-line-selected');
				return;
			}

			// Create request DTO object for communicating with backend
			this.request = Object.assign(new CreditWizardRequestModel(), {
				companyId: this.companyId!, invoiceNo: this.invoiceNumber, brandId: this.brandId
			});
			for (let line of this.creditLines!) {

				this.resetState(line); // Create basic state object for each invoice line

			}
			this.currentLine = this.creditLines![0];
		});
	}

	private isLineSuspended(line: BillingDocumentLineModel): boolean {
		let accountingPlan = this.getAccountingPlanFromLine(line);
		return accountingPlan?.suspended != undefined;
	}

	private getAccountingPlanFromLine(line: BillingDocumentLineModel): AccountingPlanModel | undefined {
		if (this.lineIsFee(line)) return;
		const ledgerPost = this.getLedgerPostFromLine(line);
		if (ledgerPost) {
			// A matching ledger post should exist for all future invoice lines except Handling Fees

			const accountingPlanId = ledgerPost.accountingPlanId;

			if (!accountingPlanId)
				return;

			let accountingPlan = this.accountingPlans.find(p => p.id && p.id === ledgerPost?.accountingPlanId);
			if (!accountingPlan)
				throw new Error(`Unable to match an accounting plan on this company (${this.companyId}) to the selected ledger post (${ledgerPost.id})`);

			return accountingPlan;
		}
		else {
			// If invoice line isn't bound to any existing ledger post (old invoices), use the "metaData" field to link accounting plan via old subscription numbers

			// If invoice line has "metaData" filled out, guess the selected accountingplan based on that information
			if (!line.metaData)
				return;

			const guessAccountingPlan = this.accountingPlans.find(p => p.subscriptionNo === line.metaData);
			if (!guessAccountingPlan)
				throw new Error(`Unable to match a Smart Subscription on this company ((${this.companyId})) to the one indicated on the selected invoice line ((${line.metaData}))`);
			return guessAccountingPlan;
		}
	}

	private getLedgerPostFromLine(line: BillingDocumentLineModel): LedgerPostModel | undefined {
		return line.lineNumber == undefined ? undefined : this.matchingLedgerPosts.find(p => p.lineNumber === line.lineNumber);
	}
	// TODO: 21531
	private lineIsFee(line: BillingDocumentLineModel): boolean {
		return (line.type === LineType.HandlingFee);
	}

	private resetState(line: BillingDocumentLineModel) {

		// Create new request and state objects for the requested invoice line

		const currentRequestLineIndex = this.request.credits.findIndex(lr => lr.invoiceLineNumber === line.lineNumber);
		const creditRequest = Object.assign(new CreditDtoModel(), {
			invoiceLineNumber: line.lineNumber,
			amount: line.totalNetAmount
		})
		if (currentRequestLineIndex < 0) this.request.credits.push(creditRequest);
		else this.request.credits.splice(currentRequestLineIndex, 1, creditRequest);

		const currentStateIndex = this.states.findIndex(s => s.invoiceLine === line);
		const state = new StepState(line, creditRequest, this.lineIsFee(line));
		if (currentStateIndex < 0) this.states.push(state);
		else this.states.splice(currentStateIndex, 1, state);

		// Determine initial values and substeps based on line details and data on the company

		if (state.lineIsFee) {

			// Only give user the option of changing customer fee settings if not already exempt
			if (!this.currentlyExemptedFromFees) this.request.noHandlingFees = false;
			else this.request.noHandlingFees = undefined;

			state.setSubSteps(CreditWizardSteps.exemptFromFee);
		}
		else {

			state.accountingPlan = this.getAccountingPlanFromLine(line);

			if (state.accountingPlan) // Credits for accounting plans are handled through the billing plan
				this.request.credits.pop();

			if (state.accountingPlan?.productPlan?.restrictEndDate)
				state.endDatesFilter = this.dateService.nextBirthdays(new Date(), state.accountingPlan?.productPlan?.restrictEndDate, 7);

			if (state.accountingPlan && !this.getLedgerPostFromLine(line) && line.metaData) {
				// If this line is not connected to a ledger post, but has a metaData field, it is likely an old invoice line from before the LedgerPost system was implemented
				state.lineRequest.accountingPlanId = state.accountingPlan.id; // Tell the backend to bind the credit line to this accounting plan
			}

			// If matching ledger post is not connected to an accounting plan (GOOGLE KLIK etc.), skip all steps involving editing the billing plan, and instead just request the amount to credit
			if (!state.accountingPlan) state.setSubSteps(CreditWizardSteps.creditAmount);
			else state.setSubSteps(CreditWizardSteps.sendNewInvoice);
		}
		if (this.currentLine == line) this.currentLine = line; // If this is the current active line, update the state to the new reset one
	}

	public maxValueToCredit(state: StepState) {
		const amount = (!this.currentLine?.totalNetAmount) ? state.lineRequest.amount : this.currentLine.totalNetAmount;
		if (!state.accountingPlan) return amount;
		return (state.accountingPlan.totalPlanValue > amount) ? amount : state.accountingPlan.totalPlanValue;
	}

	public completeCurrentSubStep(value?: boolean) {
		if (!this.stepValid || !this.currentStepState?.subStep) return;
		this.currentStepState.subStep.completed = true;
		this.stepDefinitions.get(this.currentStepState.subStep.type)?.onCompleted?.(value);

		this.currentStepState.stepIndex++;
		this.checkCompletedSubSteps();
	}
	public resetCurrentStep() {
		if (!this.currentLine) return;
		this.resetState(this.currentLine);
		this.ledgerPosts?.reloadLedgerPosts();
	}

	private checkCompletedSubSteps() {
		if (!this.creditLines || !this.currentLine || !this.currentStepState) return;
		if (this.currentStepState.stepIndex < this.currentStepState.subSteps.length) return; // More sub-steps to complete

		// Completed all sub-steps for line
		this.currentStepState.completed = true;

		// Get all edited ledger posts for this line, if ledger posts are available
		// enable edit mode for ledger posts to generate billingplan
		if (this.ledgerPosts)
			this.currentStepState.editedLedgerPosts = this.ledgerPosts.getEditedLedgerPosts(true);

		while (this.currentStepState?.completed) {
			const nextLineIndex: number = this.creditLines.indexOf(this.currentLine) + 1;

			// Move to next line (and point to its respective StepState, intentionally sets state to undefined if index exceeds list)
			this.currentLine = this.creditLines[nextLineIndex];
		}
	}

	public lineStatusColor(line: BillingDocumentLineModel): string {
		if (!this.creditLines || this.creditLines?.indexOf(line) < 0) return 'color-white';
		return this.states.find(s => s.invoiceLine === line)?.completed ? 'color-green-50' : 'color-yellow-50';
	}
	public stepValue(step: CreditWizardSteps): string {
		return this.stepDefinitions.get(step)?.getValue() ?? '';
	}

	public completeWizard() {
		if (!this.readyToComplete) return;

		this.request.billingPlans = [];
		for (let state of this.states.filter(s => s.editedLedgerPosts !== undefined)) {

			// Add new billing plan request for every line that had ledger posts modified

			const newBillingPlanRequest = new BillingPlanDtoModel();
			newBillingPlanRequest.accountingPlanId = state.accountingPlan!.id;
			newBillingPlanRequest.expectedTotalValue = state.expectedTotalValue!;
			newBillingPlanRequest.ledgerPosts = state.editedLedgerPosts!
			this.request.billingPlans.push(newBillingPlanRequest);
		}

		this.request.endAccountingPlans = [];
		for (let state of this.states.filter(s => s.endActiveAccountingPlan)) {

			// Add new request with termination and end dates for this accounting plan
			const newestAccountingPlanId = state.getNewestAccountingPlanForProduct(this.accountingPlans)!.id;
			const newEndPlanRequest = new EndAccountingPlanDtoModel();
			newEndPlanRequest.accountingPlanId = newestAccountingPlanId;
			newEndPlanRequest.endDate = state.planEndDate;
			newEndPlanRequest.terminationDate = state.planTerminationDate;
			newEndPlanRequest.terminationReasonId = state.terminationReason?.id;
			this.request.endAccountingPlans.push(newEndPlanRequest);
		}

		let promises = [];
		for (let state of this.states.filter(s => s.updateAnnualValueForRenewal)) {
			promises.push(this.productPlanService.setProductPlanAnnualValueForRenewal(state.accountingPlan!.productPlanId!, state.annualContractValue!));
		}		
		promises.push(this.accounting.createCreditMemo(this.request));
		Promise.all(promises).then(() => this.navigation.closeView(this));
	}

	private setEndDateForActiveAccountingPlan(endDate: boolean) {
		if (!this.currentLineRequest || !this.currentStepState || !this.currentStepState.accountingPlan) return;

		this.currentStepState.endActiveAccountingPlan = endDate;
		if (!endDate) {
			this.currentStepState.planEndDate = undefined;
			this.currentStepState.planTerminationDate = undefined;
		}
		else {
			this.currentStepState.planTerminationDate = this.currentStepState.existingTerminationDateForProductPlan ? undefined : this.date.today();
		}
	}

	public updateExpectedTotalValueForCurrentStep(totalLedgerValue: number) {
		// Defer update to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
		const state = this.currentStepState!;
		setTimeout(() => {
			state.expectedTotalValue = totalLedgerValue;
		});
	}

	public updateLedgerPosts(newEndDate: Date) {
		if (!this.ledgerPosts) return;
		this.ledgerPosts.disabledPosts = this.ledgerPosts.allPosts.filter(post => post.ledgerHeaderId == undefined && post.plannedDate && this.dateService.getDate(post.plannedDate) > this.dateService.getDate(newEndDate));
	}

	private checkForSubstepUpdateAnnualRenewalValue(newTcv?: number, creditValue?: number) {
		if (!this.currentStepState || !this.currentStepState.accountingPlan || !(newTcv || creditValue)) return;

		const plan = this.currentStepState.accountingPlan;
		newTcv = newTcv ? newTcv : plan.totalPlanValue - creditValue!;
		
		this.currentStepState!.annualContractValue = this.accounting.generateAnnualContractValue(plan.startDate, plan.endDate, newTcv);		
		if (!this.currentStepState.annualContractValue) return;

		if (this.currentStepState.annualContractValue != plan.productPlan.annualValueForRenewal)
			this.currentStepState.addSubSteps(CreditWizardSteps.updateAnnualValueForRenewal);
	}

	private stepDefinitions = new Map<CreditWizardSteps, StepDefinition>([
		[CreditWizardSteps.exemptFromFee, {
			getValue: () => this.translate.transform(this.currentlyExemptedFromFees ? 'credit-wizard.step2.already-exempt-from-handling-fees' : ('general.' + this.request.noHandlingFees))
		}],
		[CreditWizardSteps.creditAmount, {
			getValue: () => this.currency.transform(this.currentLineRequest?.amount),
			onCompleted: () => {

				if (!this.ledgerPosts) return;

				const creditPost = {
					value: this.currentLineRequest?.amount,
					type: LedgerPostType.CreditMemo,
					plannedDate: this.date.today(),
					creditedLineNumber: this.currentLineRequest?.invoiceLineNumber,
					creditedPostedDocumentNo: this.invoiceNumber
				};
				this.ledgerPosts.addPost(creditPost);
				this.currentStepState!.allLedgerPosts = [...this.ledgerPosts!.allPosts];

				this.checkForSubstepUpdateAnnualRenewalValue(undefined, creditPost.value);
			}
		}],
		[CreditWizardSteps.updateAnnualValueForRenewal, {
			isTrueFalse: true,
			getValue: () => this.currentStepState!.updateAnnualValueForRenewal === undefined ? '' : this.translate.transform('general.' + this.currentStepState!.updateAnnualValueForRenewal),
			onCompleted: (updateAnnualValueForRenewal?: boolean) => {
				this.currentStepState!.updateAnnualValueForRenewal = updateAnnualValueForRenewal;
			}
		}],
		[CreditWizardSteps.newEndDate, {
			getValue: () => this.date.transform(this.currentStepState!.planEndDate, 'shortDate')
		}],
		[CreditWizardSteps.terminationReason, {
			getValue: () => this.currentStepState!.terminationReason?.name ? this.translate.transform("termination-reason." + this.currentStepState!.terminationReason!.name) : undefined
		}],
		[CreditWizardSteps.sendNewInvoice, {
			isTrueFalse: true,
			getValue: () => this.currentStepState!.sendNewInvoice === undefined ? '' : this.translate.transform('general.' + this.currentStepState!.sendNewInvoice),
			onCompleted: (sendNewInvoice?: boolean) => {

				this.currentStepState!.sendNewInvoice = sendNewInvoice;

				if (sendNewInvoice) {
					this.currentStepState!.newInvoiceAmount = this.currentLine?.totalNetAmount;

					let sameAmountsForRemainingInvoicePosts = [...new Set(this.ledgerPosts!.posts.filter(post => (post.type == LedgerPostType.Invoice || post.type == LedgerPostType.StartupInvoice) && !post.ledgerHeader).map(post => post.signedValue))].length == 1;

					if (sameAmountsForRemainingInvoicePosts) {
						this.currentStepState!.addSubSteps(CreditWizardSteps.newInvoiceAmount, CreditWizardSteps.changeRemainingInvoicePosts, CreditWizardSteps.editLedgerPosts);
					}
					else {
						this.currentStepState!.addSubSteps(CreditWizardSteps.newInvoiceAmount, CreditWizardSteps.editLedgerPosts);
					}
				}
				else {
					const today = this.dateService.today();
					const newestAccountingPlan = this.currentStepState!.getNewestAccountingPlanForProduct(this.accountingPlans);

					if (newestAccountingPlan?.endDate && newestAccountingPlan.endDate >= today) this.currentStepState!.addSubSteps(CreditWizardSteps.creditAmount, CreditWizardSteps.endActiveAccountingPlan);
					else this.currentStepState!.addSubSteps(CreditWizardSteps.creditAmount);
				}
			}
		}],
		[CreditWizardSteps.newInvoiceAmount, {
			getValue: () => this.currency.transform(this.currentStepState!.newInvoiceAmount),
			onCompleted: () => {

				// Edit ledger post edit-view with the new invoice amount. Add currently added credit memo to view as well (non-editable!)
				const newInvoiceValue = this.currentStepState!.newInvoiceAmount ?? 0;
				const invoicePost = {
					value: newInvoiceValue,
					type: LedgerPostType.Invoice,
					plannedDate: this.date.today()
				};
				const creditPost = {
					value: this.currentLineRequest?.amount ?? 0,
					type: LedgerPostType.CreditMemo,
					plannedDate: this.date.today(),
					creditedLineNumber: this.currentLineRequest?.invoiceLineNumber,
					creditedPostedDocumentNo: this.invoiceNumber
				};
				this.ledgerPosts!.addPost(creditPost);
				this.ledgerPosts!.addPost(invoicePost);
				this.currentStepState!.allLedgerPosts = [...this.ledgerPosts!.allPosts];
			}
		}],
		[CreditWizardSteps.changeRemainingInvoicePosts, {
			isTrueFalse: true,
			getValue: () => this.currentStepState!.changeRemainingInvoicePosts === undefined ? '' : this.translate.transform('general.' + this.currentStepState!.changeRemainingInvoicePosts),
			onCompleted: (value?: boolean) => {
				this.currentStepState!.changeRemainingInvoicePosts = value;
				if (value) {
					const newInvoiceValue = this.currentStepState?.newInvoiceAmount ?? 0;
					this.ledgerPosts!.posts.filter(post => this.ledgerPosts!.isEditable(post, true)).forEach(post => {
						post.value = newInvoiceValue;
					});
					this.ledgerPosts!.calculateLedgerValues();
				}
			}
		}],
		[CreditWizardSteps.endActiveAccountingPlan, {
			isTrueFalse: true,
			getValue: () => this.currentStepState!.endActiveAccountingPlan === undefined ? '' : this.translate.transform('general.' + this.currentStepState!.endActiveAccountingPlan),
			onCompleted: (value?: boolean) => {
				this.setEndDateForActiveAccountingPlan(value!);
				if (value) {
					this.currentStepState!.addSubSteps(CreditWizardSteps.newEndDate, CreditWizardSteps.terminationReason);					
				}
			}
		}],
		[CreditWizardSteps.editLedgerPosts, {
			getValue: () => this.currency.transform(this.currentStepState?.expectedTotalValue),
			onCompleted: () => {
				this.currentStepState!.allLedgerPosts = [...this.ledgerPosts!.allPosts];
								
				this.checkForSubstepUpdateAnnualRenewalValue(this.currentStepState!.expectedTotalValue, undefined);
			}
		}],
	]);
}

interface StepDefinition {
	getValue: () => string | undefined;
	onCompleted?: (value?: boolean) => void;
	isTrueFalse?: boolean;
}
class StepState {
	completed = false;
	invoiceLine: BillingDocumentLineModel;
	lineRequest: CreditDtoModel;
	stepIndex = 0;
	sendNewInvoice?: boolean;
	changeRemainingInvoicePosts?: boolean;
	lineIsFee: boolean;
	endActiveAccountingPlan?: boolean;
	existingTerminationDateForProductPlan = undefined;
	newInvoiceAmount?: number;
	planEndDate?: Date;
	terminationReason?: ReasonModel;
	planTerminationDate?: Date;
	allLedgerPosts?: LedgerPostModel[];
	editedLedgerPosts?: LedgerPostModel[];
	expectedTotalValue?: number;
	updateAnnualValueForRenewal?: boolean;
	annualContractValue?: number;
	endDatesFilter?: Date[];
	
	public subSteps: { type: CreditWizardSteps, completed: boolean }[] = [];
	public get subStep(): { type: CreditWizardSteps, completed: boolean } | undefined { return this.subSteps[this.stepIndex]; }

	private _accountingPlan?: AccountingPlanModel;
	public set accountingPlan(value: AccountingPlanModel | undefined) {
		this._accountingPlan = value;
		this.existingTerminationDateForProductPlan = value?.productPlan?.terminationDate;
	}
	public get accountingPlan() { return this._accountingPlan; }

	constructor(invoiceLine: BillingDocumentLineModel, lineRequest: CreditDtoModel, lineIsFee: boolean) {
		this.invoiceLine = invoiceLine;
		this.lineRequest = lineRequest;
		this.lineIsFee = lineIsFee;
	}

	public setSubSteps(...stepTypes: CreditWizardSteps[]) {
		this.subSteps = stepTypes.map((stepType) => { return { type: stepType, completed: false } });
	}
	public addSubSteps(...stepTypes: CreditWizardSteps[]) {
		for (let stepType of stepTypes) this.subSteps.push({ type: stepType, completed: false });
	}

	public getNewestAccountingPlanForProduct(accountingPlans: AccountingPlanModel[]): AccountingPlanModel | undefined {
		const productPlanId = this.accountingPlan?.productPlanId;
		if (!productPlanId) throw new Error('unable to match accountingplan to a product');

		const sortedPlans = accountingPlans.filter(plan => plan.endDate);
		sortedPlans.sort((a, b) => { return b.endDate!.getTime() - a.endDate!.getTime() });
		return sortedPlans.find(plan => plan.productPlanId == productPlanId);
	}
}
enum CreditWizardSteps {
	sendNewInvoice, creditAmount, endActiveAccountingPlan, newEndDate, terminationReason, updateAnnualValueForRenewal, exemptFromFee, newInvoiceAmount, editLedgerPosts, changeRemainingInvoicePosts
}
